/**
 * LayoutComponentPropertiesPanel - Bridge between PageBuilder and PropertiesPanel
 *
 * This component adapts the layout section's component tree structure
 * to work with the new config-driven PropertiesPanel.
 *
 * It handles:
 * - Extracting the selected component from the tree using path
 * - Converting single-prop updates to full tree updates via actualColumns
 * - Preserving existing PageBuilder update flow
 * - Extracting column span data for custom layout editing
 */

import React, { useMemo, useCallback } from 'react';
import PropertiesPanel from './PropertiesPanel';
import { getComponentDefinition } from '../componentDefinitions';
import { getComponentAtPath, getParentPath } from './utils/treeUtils';
import { usePageBuilder } from './context/PageBuilderContext';

/**
 * LayoutComponentPropertiesPanel - Adapter for layout section components
 *
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 */
export default function LayoutComponentPropertiesPanel({
  className = '',
}) {
  const { doc, selection, responsive, page } = usePageBuilder();
  const section = selection.selectedSection;
  const onUpdate = doc.handleQuickUpdate;
  const onUpdateComponentProp = doc.updateComponentProp;
  const activeBreakpoint = responsive.breakpoint;
  const onBreakpointChange = responsive.setBreakpoint;
  // Extract the selected component from the tree
  const selectedComponent = useMemo(() => {
    const components = section?.content?.components;
    const path = section?.content?.selectedComponentPath;

    if (!components || !path) {
      return null;
    }

    const component = getComponentAtPath(components, path);

    if (!component) {
      return null;
    }

    // Return in the format PropertiesPanel expects
    return {
      id: component.id,
      type: component.type,
      props: component.props || {},
      children: component.children || [],
    };
  }, [section?.content?.components, section?.content?.selectedComponentPath]);

  // Extract parent component props (used for conditional field visibility, e.g. flexChild)
  const parentProps = useMemo(() => {
    const components = section?.content?.components;
    const path = section?.content?.selectedComponentPath;
    if (!components || !path) return null;

    const parentPath = getParentPath(path);
    if (!parentPath) return null; // root-level, no parent

    const parent = getComponentAtPath(components, parentPath);
    return parent?.props || null;
  }, [section?.content?.components, section?.content?.selectedComponentPath]);

  // Extract column span data when the selected component is a row
  const currentColumnSpans = useMemo(() => {
    if (!selectedComponent || selectedComponent.type !== 'row') {
      return null;
    }

    const children = selectedComponent.children || [];
    return children
      .filter((child) => child.type === 'column')
      .map((child) => {
        const span = child.props?.span;
        // Normalize to responsive format
        if (typeof span === 'object' && span !== null && span.base !== undefined) {
          return span;
        }
        // Legacy number format
        return { base: span || 6, tablet: null, desktop: null };
      });
  }, [selectedComponent]);

  // Direct component prop update — uses the hot-path API when available,
  // falls back to the actualColumns indirection for backward compatibility.
  const handlePropertyUpdate = useCallback(
    (key, value) => {
      if (!section?.id) return;
      const componentPath = section?.content?.selectedComponentPath;
      if (onUpdateComponentProp && componentPath) {
        onUpdateComponentProp(section.id, componentPath, { [key]: value });
      } else {
        onUpdate(section.id, { actualColumns: [{ [key]: value }] });
      }
    },
    [section?.id, section?.content?.selectedComponentPath, onUpdate, onUpdateComponentProp]
  );

  // Handler for custom column span changes (via dragging or numeric input)
  const handleCustomSpansChange = useCallback(
    (breakpoint, newSpansArray) => {
      if (!section?.id) return;

      const selectedPath = section?.content?.selectedComponentPath;
      if (!selectedPath) return;

      onUpdate(section.id, {
        layoutAction: 'updateColumnSpans',
        rowPath: selectedPath,
        breakpoint,
        spans: newSpansArray,
      });
    },
    [section?.id, section?.content?.selectedComponentPath, onUpdate]
  );

  // Handler for adding a column to the row
  const handleAddColumn = useCallback(() => {
    if (!section?.id) return;

    const selectedPath = section?.content?.selectedComponentPath;
    if (!selectedPath) return;

    onUpdate(section.id, {
      layoutAction: 'addColumnToRow',
      rowPath: selectedPath,
    });
  }, [section?.id, section?.content?.selectedComponentPath, onUpdate]);

  // Handler for removing a column from the row
  const handleRemoveColumn = useCallback(
    (columnIndex) => {
      if (!section?.id) return;

      const selectedPath = section?.content?.selectedComponentPath;
      if (!selectedPath) return;

      onUpdate(section.id, {
        layoutAction: 'removeColumnFromRow',
        rowPath: selectedPath,
        columnIndex,
      });
    },
    [section?.id, section?.content?.selectedComponentPath, onUpdate]
  );

  // No component selected
  if (!selectedComponent) {
    return null;
  }

  // Check if we have a definition for this component type
  const definition = getComponentDefinition(selectedComponent.type);
  if (!definition) {
    // Fall back to null - QuickPropertiesPanel will handle unknown types
    return null;
  }

  return (
    <PropertiesPanel
      selectedComponent={selectedComponent}
      onUpdate={handlePropertyUpdate}
      className={className}
      activeBreakpoint={activeBreakpoint}
      onBreakpointChange={onBreakpointChange}
      page={page}
      parentProps={parentProps}
      currentColumnSpans={currentColumnSpans}
      onCustomSpansChange={handleCustomSpansChange}
      onAddColumn={handleAddColumn}
      onRemoveColumn={handleRemoveColumn}
    />
  );
}

/**
 * Check if the new properties panel can handle this section/component
 *
 * @param {Object} section - Section to check
 * @returns {boolean} True if LayoutComponentPropertiesPanel can handle it
 */
export function canHandleLayoutComponent(section) {
  if (section?.section_type !== 'layout-1') {
    return false;
  }

  const path = section?.content?.selectedComponentPath;
  const components = section?.content?.components;

  if (!path || !components) {
    return false;
  }

  const component = getComponentAtPath(components, path);
  if (!component?.type) {
    return false;
  }

  // Check if we have a definition for this component
  const definition = getComponentDefinition(component.type);
  return !!definition;
}
